/**
 * DIRECT VARIANT PRICE UPDATER
 * 
 * This script directly updates the price when variants change
 * without relying on HTML fetching/replacement
 * 
 * Now updates only the price AMOUNT, keeping LE and EGP in the HTML
 * 
 * Installation: Add this to your theme's assets folder as variant-price-updater.js
 * Then include it in main-product.liquid
 */

(function() {
  'use strict';

  console.log('🔧 Variant Price Updater Initialized');

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Get the product data
    const productInfo = document.querySelector('product-info');
    if (!productInfo) {
      console.warn('⚠️ product-info element not found');
      return;
    }

    // Get all variant data from the page
    const productData = getProductData();
    if (!productData) {
      console.warn('⚠️ Could not extract product data');
      return;
    }

    // Setup listeners for variant changes
    setupVariantListeners(productData);
  }

  /**
   * Extract product and variant data from the page
   */
  function getProductData() {
    // Try to get data from script tag
    let scriptTag = document.querySelector('script[type="application/json"][data-product-json]');
    if (!scriptTag) {
      // Fallback: try other methods
      scriptTag = document.querySelector('script#ProductJson-template--product');
      if (!scriptTag) {
        scriptTag = document.querySelector('script[type="application/json"]');
      }
    }

    if (scriptTag) {
      try {
        return JSON.parse(scriptTag.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }

    // If no JSON found, build data from form
    return buildProductDataFromForm();
  }

  /**
   * Build product data from form elements (fallback)
   */
  function buildProductDataFromForm() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;

    const variants = [];
    const productInfo = document.querySelector('product-info');
    
    // This is a simplified version - we'll rely on variant-selects data
    return {
      variants: variants,
      available: true
    };
  }

  /**
   * Setup listeners for all variant selection methods
   */
  function setupVariantListeners(productData) {
    // Method 1: Listen to variant-selects custom element
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      console.log('✅ Found variant-selects element');
      
      // Listen for changes on all option inputs
      const optionInputs = variantSelects.querySelectorAll('input[name^="options"], select[name^="options"]');
      optionInputs.forEach(input => {
        input.addEventListener('change', () => {
          console.log('🔄 Variant option changed');
          handleVariantChange(productData);
        });
      });
    }

    // Method 2: Listen to hidden variant ID input
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput) {
      console.log('✅ Found variant ID input');
      variantIdInput.addEventListener('change', () => {
        console.log('🔄 Variant ID changed to:', variantIdInput.value);
        updatePriceFromVariantId(variantIdInput.value, productData);
      });
    }

    // Method 3: Listen to PubSub events (if available)
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      console.log('✅ PubSub system available');
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        console.log('🔄 PubSub variant change event');
        if (event.data && event.data.variant) {
          updatePriceDisplay(event.data.variant);
        }
      });
    }

    // Initial update
    handleVariantChange(productData);
  }

  /**
   * Handle variant change - find selected variant and update price
   */
  function handleVariantChange(productData) {
    const selectedVariant = getSelectedVariant(productData);
    if (selectedVariant) {
      console.log('✅ Selected variant:', selectedVariant.id, '- Price:', selectedVariant.price);
      updatePriceDisplay(selectedVariant);
    }
  }

  /**
   * Update price from variant ID
   */
  function updatePriceFromVariantId(variantId, productData) {
    const variant = productData.variants.find(v => v.id == variantId);
    if (variant) {
      console.log('✅ Found variant by ID:', variant.id);
      updatePriceDisplay(variant);
    }
  }

  /**
   * Get the currently selected variant
   */
  function getSelectedVariant(productData) {
    // Try to get from hidden input first
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput && variantIdInput.value) {
      const variant = productData.variants.find(v => v.id == variantIdInput.value);
      if (variant) return variant;
    }

    // Fallback: get from option selections
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;

    const selectedOptions = [];
    const optionInputs = variantSelects.querySelectorAll('input[name^="options"]:checked, select[name^="options"]');
    
    optionInputs.forEach(input => {
      selectedOptions.push(input.value);
    });

    // Find variant matching selected options
    return productData.variants.find(variant => {
      if (!variant.options) return false;
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  /**
   * Update the price display with variant data
   */
  function updatePriceDisplay(variant) {
    console.log('💰 Updating price display for variant:', variant.id);

    // Find all price containers
    const priceContainers = document.querySelectorAll('.price, [id^="price-"], [id^="Price-"]');
    
    priceContainers.forEach(container => {
      updatePriceContainer(container, variant);
    });

    // Update availability badge if needed
    updateAvailability(variant);
  }

  /**
   * Update a single price container
   * Now only updates the .price-amount spans, leaving LE and EGP in place
   */
  function updatePriceContainer(container, variant) {
    const price = variant.price;
    const compareAtPrice = variant.compare_at_price;
    const available = variant.available;

    // Format price amounts (numbers only, no currency)
    const formattedPrice = formatMoney(price);
    const formattedComparePrice = compareAtPrice ? formatMoney(compareAtPrice) : null;

    console.log('Formatted prices:', {
      price: formattedPrice,
      compareAt: formattedComparePrice,
      available: available
    });

    // Update regular price (no sale) - find price-amount span
    const regularPriceContainer = container.querySelector('.price__regular');
    if (regularPriceContainer) {
      const priceAmountSpan = regularPriceContainer.querySelector('.price-amount[data-regular-price]');
      if (priceAmountSpan && !compareAtPrice) {
        priceAmountSpan.textContent = formattedPrice;
        console.log('✅ Updated regular price amount');
      }
    }

    // Update sale price - find price-amount spans
    const salePriceContainer = container.querySelector('.price__sale');
    if (salePriceContainer && compareAtPrice) {
      // Update compare-at price amount (strikethrough)
      const compareAmountSpan = salePriceContainer.querySelector('.price-amount[data-compare-price]');
      if (compareAmountSpan) {
        compareAmountSpan.textContent = formattedComparePrice;
      }

      // Update sale price amount
      const saleAmountSpan = salePriceContainer.querySelector('.price-amount[data-sale-price]');
      if (saleAmountSpan) {
        saleAmountSpan.textContent = formattedPrice;
        console.log('✅ Updated sale price amount');
      }
    }

    // Toggle price display classes
    if (compareAtPrice && compareAtPrice > price) {
      container.classList.add('price--on-sale');
      container.classList.remove('price--sold-out');
      if (regularPriceContainer) regularPriceContainer.style.display = 'none';
      if (salePriceContainer) salePriceContainer.style.display = 'flex';
    } else {
      container.classList.remove('price--on-sale');
      if (regularPriceContainer) regularPriceContainer.style.display = 'flex';
      if (salePriceContainer) salePriceContainer.style.display = 'none';
    }

    // Handle sold out
    if (!available) {
      container.classList.add('price--sold-out');
    } else {
      container.classList.remove('price--sold-out');
    }

    // Update sale badge visibility
    const saleBadge = container.querySelector('.price__badge-sale');
    if (saleBadge) {
      saleBadge.style.display = (compareAtPrice && compareAtPrice > price) ? 'inline-block' : 'none';
    }

    // Update sold out badge visibility
    const soldOutBadge = container.querySelector('.price__badge-sold-out');
    if (soldOutBadge) {
      soldOutBadge.style.display = !available ? 'inline-block' : 'none';
    }
  }

  /**
   * Update availability information
   */
  function updateAvailability(variant) {
    // Update inventory status
    const inventoryElement = document.querySelector('[id^="Inventory-"]');
    if (inventoryElement && variant.inventory_management === 'shopify') {
      // Update inventory display based on variant
      // This would require additional inventory data
    }

    // Update add to cart button
    const submitButton = document.querySelector('button[name="add"], button[type="submit"][form]');
    if (submitButton) {
      const submitButtonText = submitButton.querySelector('span');
      if (variant.available) {
        submitButton.removeAttribute('disabled');
        if (submitButtonText && window.variantStrings && window.variantStrings.addToCart) {
          submitButtonText.textContent = window.variantStrings.addToCart;
        }
      } else {
        submitButton.setAttribute('disabled', 'disabled');
        if (submitButtonText && window.variantStrings && window.variantStrings.soldOut) {
          submitButtonText.textContent = window.variantStrings.soldOut;
        }
      }
    }
  }

  /**
   * Format money - returns just the number with proper formatting
   * Currency symbols (LE and EGP) are already in the HTML via Liquid
   * This only formats the numeric amount with commas and decimals
   */
  function formatMoney(cents) {
    // Convert cents to amount and format with commas
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return amount;
  }

})();